import React, { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  ButtonBase,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import { Bell } from 'lucide-react';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useAuth from 'src/hooks/useAuth';
import useSelectedSubAccount from 'src/hooks/useSelectedSubAccount';
import GetSubAccountAlerts from 'src/api/GetSubAccountAlerts';
import { BTC } from 'src/constants';

const useStyles = makeStyles()((theme) => ({
  bellButton: {
    padding: 8,
    borderRadius: '8px',
    '&:hover': {
      backgroundColor: 'var(--neutral-3)',
    },
  },
  popover: {
    marginTop: theme.spacing(1),
    width: 320,
    backgroundColor: 'var(--neutral-2)',
    border: '1px solid var(--neutral-3)',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  alertList: {
    maxHeight: 340,
    overflowY: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: '#6b7280 transparent',
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#6b7280',
      borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#9ca3af',
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: '14px',
    color: '#F5F5F6',
  },
  viewAll: {
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--accent-9)',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  alertItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '10px 16px',
    gap: '2px',
    whiteSpace: 'normal',
    '&:hover': {
      backgroundColor: 'var(--neutral-3)',
    },
  },
  alertSubAccount: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#E9EBEA',
    width: '100%',
  },
  alertDetail: {
    fontSize: '12px',
    color: 'var(--color-text)',
    width: '100%',
    wordBreak: 'break-word',
  },
  emptyState: {
    padding: '24px 16px',
    textAlign: 'center',
    color: '#9999A1',
    fontSize: '13px',
    fontFamily: theme.typography.fontFamily,
  },
}));

const AlertsBell = () => {
  const { classes } = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ref = useRef(null);
  const { loggedInUser, getAccessToken } = useAuth();
  const { handleSelectedSubAccountUpdate } = useSelectedSubAccount();
  const [isOpen, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const coin = BTC;

  const allGroups = loggedInUser?.groups?.map((g) => ({ group: { id: g.group.id, name: g.group.name } })) || [];

  const viewHashRatePermission =
    loggedInUser?.groups
      ?.map((g) => g.defaultSubAccountRole.viewHashrate)
      ?.filter((p) => !!p)?.length > 0;

  useEffect(() => {
    const fetchAlerts = async () => {
      const { tokenAvailable, accessToken } = await getAccessToken();
      if (!tokenAvailable) return;
      const { success, data } = await GetSubAccountAlerts(
        accessToken,
        coin,
        'ascSubAccount',
        allGroups,
        true
      );
      if (success) {
        setAlerts(data);
      }
    };
    if (viewHashRatePermission) fetchAlerts();
  }, [coin]);

  if (!viewHashRatePermission) return null;

  const activeCount = alerts.length;

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);


  const handleAlertClick = (alert) => {
    handleClose();
    handleSelectedSubAccountUpdate(alert.subAccountName);
    navigate(`/app/sub-accounts/${alert.subAccountName}/dashboard`, {
      state: { root: t('subAccounts'), group: alert.groupName },
    });
  };

  const formatAlertType = (alert) => {
    const types = [];
    if (alert.alertType?.hashrate) {
      types.push(
        `Hashrate ${alert.alertInfo?.hashrate ? `at ${alert.alertInfo.hashrate}` : 'below threshold'} (threshold: ${alert.hashrateThreshold})`
      );
    }
    if (alert.alertType?.workers) {
      types.push(
        `Active workers: ${alert.alertInfo?.workers ?? '-'} (threshold: ${alert.activeWorkersThreshold})`
      );
    }
    return types.join(' | ') || 'Alert triggered';
  };

  return (
    <>
      <IconButton
        data-testid="fdr_alerts_bell_btn"
        ref={ref}
        onClick={handleOpen}
        className={classes.bellButton}
        size="small"
      >
        <Badge
          badgeContent={activeCount}
          color="warning"
          max={99}
          invisible={activeCount === 0}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '11px',
              height: '18px',
              minWidth: '18px',
              backgroundColor: '#F59E0B',
              color: '#000',
              fontWeight: 700,
            },
          }}
        >
          <Bell size={20} color="#A3ACA9" />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={ref.current}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ className: classes.popover }}
      >
        <Box className={classes.header}>
          <Typography className={classes.headerTitle}>
            {t('alerts')} {activeCount > 0 && `(${activeCount})`}
          </Typography>
          <Typography data-testid="fdr_alerts_bell_link_manage" className={classes.viewAll} onClick={() => { handleClose(); navigate('/app/alerts/manage'); }}>
            Manage
          </Typography>
        </Box>
        <Divider sx={{ borderColor: 'var(--neutral-3)' }} />
        <Box className={classes.alertList}>
        {alerts.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography style={{ fontSize: '13px', marginBottom: '8px' }}>
              No active alerts
            </Typography>
            <Typography style={{ fontSize: '12px', color: 'var(--colors-text-text-secondary-700, #9999A1)' }}>
              Set up alerts to get notified when hashrate drops or workers go offline.
            </Typography>
          </Box>
        ) : (
          alerts.map((alert) => (
            <MenuItem
              key={alert.alertId}
              className={classes.alertItem}
              onClick={() => handleAlertClick(alert)}
            >
              <Typography className={classes.alertSubAccount}>
                {alert.subAccountName}
              </Typography>
              <Typography className={classes.alertDetail}>
                {formatAlertType(alert)}
              </Typography>
              <Typography className={classes.alertDetail} style={{ opacity: 0.7 }}>
                {alert.groupName}
              </Typography>
            </MenuItem>
          ))
        )}
        </Box>
      </Menu>
    </>
  );
};

export default AlertsBell;
